import axios, { AxiosInstance } from 'axios';
import Logger from '../Logger/Logger';
import StorageServices from '../Storage/StorageServices';
import Validator from '../Validator/Validator';
import { HttpResultPojo } from './HttpResultPojo';
import { callLogoutApi } from '../Logout/Logout';
import { logout } from '../../features/authSlice';
import { getGlobalDispatch, getGlobalNavigate } from '../ReactServiceContext';
import { Language } from '../../language/English';

type LogoutOptions = {
    logoutRedirectUrl?: string;
    logoutCallback?: () => void;
    dispatch?: any;
    navigate?: ((to: string) => void) | null;
};

export default class HttpService {
    private authorizationToken;
    private validator = new Validator();
    private logger = new Logger();
    private storageServices = new StorageServices();
    private logoutOptions?: LogoutOptions;
    private axiosInstance: AxiosInstance;
    private dispatch: any;
    private navigate: ((to: string) => void) | null;

    constructor(options?: LogoutOptions) {
        this.logoutOptions = {
            logoutRedirectUrl: '/logout',
            ...options,
        };
        this.dispatch = options?.dispatch ?? getGlobalDispatch();
        this.navigate = options?.navigate ?? getGlobalNavigate();
        const userToken = this.storageServices.get('user');
        this.authorizationToken = null;
        if (userToken !== undefined && userToken !== null) {
            try {
                this.authorizationToken = JSON.parse(userToken).token;
            } catch (e) {
                this.logger.error(e);
                this.authorizationToken = null;
            }
        }

        this.axiosInstance = axios.create();

        this.axiosInstance.interceptors.response.use(
            (response) => response,
            (error) => {
                this.handleError(error);
                return Promise.reject(error);
            },
        );
    }

    private handleLogout = async () => {
        const dispatchToUse = this.dispatch ?? getGlobalDispatch();
        const navigateToUse = this.navigate ?? getGlobalNavigate();
        const safeNavigate = navigateToUse
            ? navigateToUse
            : (url: string) => {
                  window.location.href = url;
              };
        try {
            callLogoutApi(dispatchToUse, () => logout(), safeNavigate);
        } catch (e) {
            this.logger.error(e);
        }
    };

    private handleUnauthorized = (): void => {
        try {
            if (typeof (this.storageServices as any).remove === 'function') {
                (this.storageServices as any).remove('user');
            } else {
                try {
                    localStorage.removeItem('user');
                } catch (e) {}
            }
        } catch (e) {
            this.logger.error(e);
        }

        this.authorizationToken = null;

        if (
            this.logoutOptions &&
            typeof this.logoutOptions.logoutCallback === 'function'
        ) {
            try {
                this.logoutOptions.logoutCallback();
                return;
            } catch (err) {
                this.logger.error(err);
            }
        }

        if (this.logoutOptions && this.logoutOptions.logoutRedirectUrl) {
            try {
                this.handleLogout();
                window.location.href = this.logoutOptions.logoutRedirectUrl;
                return;
            } catch (err) {
                this.logger.error(err);
            }

            try {
                const navigateToUse = this.navigate ?? getGlobalNavigate();
                this.handleLogout();
                if (navigateToUse) {
                    navigateToUse(this.logoutOptions.logoutRedirectUrl);
                } else {
                    window.location.href = this.logoutOptions.logoutRedirectUrl;
                }
                return;
            } catch (err) {
                this.logger.error(err);
            }
        }

        try {
            this.handleLogout();
            window.location.reload();
        } catch (err) {
            this.logger.error(err);
        }
    };

    private isRequestSuccessful(status: number): boolean {
        return status >= 200 && status <= 299 ? true : false;
    }

    private handleError(e: any): void {
        if (
            e &&
            e.response &&
            e.response.status === 401 &&
            e?.response?.data?.messageForUser === Language.TOKEN_IS_INVALID
        ) {
            window.location.href = '/unauthorized';
        } else {
            this.handleUnauthorized();
        }
    }
    async get(apiEndpoint: string, params: object, config: object = {}): Promise<HttpResultPojo> {
        try {
            this.validator.isValidString('apiEndpoint', apiEndpoint);
            const result = await axios.get(apiEndpoint, {
                params,
                headers: {
                    Authorization: `Bearer ${this.authorizationToken}`,
                },
                ...config,
            });
            return {
                data: result.data,
                status: result.status,
                isRequestSuccessful: this.isRequestSuccessful(result.status),
            };
        } catch (e: any) {
            this.logger.error(e);
            this.handleError(e);
            throw e;
        }
    }
    async post(apiEndpoint: string, params: object): Promise<HttpResultPojo> {
        try {
            this.validator.isValidString('apiEndpoint', apiEndpoint);
            const result = await axios.post(apiEndpoint, params, {
                headers: { Authorization: `Bearer ${this.authorizationToken}` },
            });
            return {
                data: result.data,
                status: result.status,
                isRequestSuccessful: this.isRequestSuccessful(result.status),
            };
        } catch (e: any) {
            this.logger.error(e);
            this.handleError(e);
            throw e;
        }
    }
    async postFormData(
        apiEndpoint: string,
        formData: any,
    ): Promise<HttpResultPojo> {
        try {
            this.validator.isValidString('apiEndpoint', apiEndpoint);
            const result = await axios.post(apiEndpoint, formData, {
                headers: {
                    'content-type': 'multipart/form-data',
                    Authorization: `Bearer ${this.authorizationToken}`,
                },
            });
            return {
                data: result.data,
                status: result.status,
                isRequestSuccessful: this.isRequestSuccessful(result.status),
            };
        } catch (e: any) {
            this.logger.error(e);
            this.handleError(e);
            throw e;
        }
    }
    async put(apiEndpoint: string, params: object): Promise<HttpResultPojo> {
        try {
            this.validator.isValidString('apiEndpoint', apiEndpoint);
            const result = await axios.put(apiEndpoint, params, {
                headers: { Authorization: `Bearer ${this.authorizationToken}` },
            });
            return {
                data: result.data,
                status: result.status,
                isRequestSuccessful: this.isRequestSuccessful(result.status),
            };
        } catch (e: any) {
            this.logger.error(e);
            this.handleError(e);
            throw e;
        }
    }
    async delete(apiEndpoint: string, params: object): Promise<HttpResultPojo> {
        try {
            this.validator.isValidString('apiEndpoint', apiEndpoint);
            const result = await axios.delete(apiEndpoint, {
                headers: { Authorization: `Bearer ${this.authorizationToken}` },
                params,
            });
            return {
                data: result.data,
                status: result.status,
                isRequestSuccessful: this.isRequestSuccessful(result.status),
            };
        } catch (e: any) {
            this.logger.error(e);
            this.handleError(e);
            throw e;
        }
    }
}
